import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  SafeAreaView,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Linking,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useThemeColors, useThemedStyles } from '@/hooks/shared/useThemeColors';
import type { ThemeContextValue } from '@/hooks/shared/useThemeColors';
import { AppConfig } from '@/constants/residents/config';
import { AuthStackParamList } from '@/navigation/residents/mobile/AuthNavigator';

export type LegalContentType = 'privacy' | 'terms';

type LegalRouteProp = RouteProp<AuthStackParamList, 'Legal'>;
type LegalNavigationProp = StackNavigationProp<AuthStackParamList, 'Legal'>;

interface LegalScreenProps {
  contentType?: LegalContentType;
}

interface ParsedSection {
  type: 'heading' | 'subheading' | 'paragraph';
  text: string;
}

const stripHtmlTags = (html: string): string => {
  return html.replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&nbsp;/g, ' ');
};

const parseHtmlToSections = (html: string): ParsedSection[] => {
  const sections: ParsedSection[] = [];
  const headingRegex = /<h([1-6])[^>]*>([\s\S]*?)<\/h\1>/gi;
  const paragraphRegex = /<p[^>]*>([\s\S]*?)<\/p>/gi;
  const listItemRegex = /<li[^>]*>([\s\S]*?)<\/li>/gi;

  const tokens: { index: number; section: ParsedSection }[] = [];

  let match;
  while ((match = headingRegex.exec(html)) !== null) {
    const level = parseInt(match[1], 10);
    tokens.push({
      index: match.index,
      section: {
        type: level <= 2 ? 'heading' : 'subheading',
        text: stripHtmlTags(match[2]).trim(),
      },
    });
  }

  while ((match = paragraphRegex.exec(html)) !== null) {
    const text = stripHtmlTags(match[1]).trim();
    if (text) {
      tokens.push({
        index: match.index,
        section: { type: 'paragraph', text },
      });
    }
  }

  while ((match = listItemRegex.exec(html)) !== null) {
    const text = stripHtmlTags(match[1]).trim();
    if (text) {
      tokens.push({
        index: match.index,
        section: { type: 'paragraph', text: `• ${text}` },
      });
    }
  }

  tokens.sort((a, b) => a.index - b.index);

  if (tokens.length === 0) {
    const plainText = stripHtmlTags(html).trim();
    if (plainText) {
      const lines = plainText.split(/\n+/).filter(line => line.trim());
      for (const line of lines) {
        sections.push({ type: 'paragraph', text: line.trim() });
      }
    }
    return sections;
  }

  return tokens.map(t => t.section);
};

export const LegalScreen: React.FC<LegalScreenProps> = ({ contentType: propContentType }) => {
  const navigation = useNavigation<LegalNavigationProp>();
  const route = useRoute<LegalRouteProp>();
  const { colors, semantic } = useThemeColors();
  const styles = useThemedStyles(makeStyles);
  const [isLoading, setIsLoading] = useState(true);
  const [sections, setSections] = useState<ParsedSection[]>([]);
  const [error, setError] = useState<string | null>(null);
  
  const contentType = route.params?.contentType || propContentType || 'privacy';
  
  const getTitle = (): string => {
    switch (contentType) {
      case 'privacy':
        return 'Privacy Policy';
      case 'terms':
        return 'Terms & Conditions';
      default:
        return 'Legal';
    }
  };

  const getUrl = (): string => {
    return contentType === 'terms' ? AppConfig.URLS.TERMS : AppConfig.URLS.PRIVACY;
  };

  const handleBackPress = () => {
    navigation.goBack();
  };

  const handleOpenInBrowser = () => {
    Linking.openURL(getUrl());
  };

  const fetchContent = useCallback(async () => {
    const url = contentType === 'terms' ? AppConfig.URLS.TERMS : AppConfig.URLS.PRIVACY;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const html = await response.text();
      setSections(parseHtmlToSections(html));
    } catch (err) {
      console.error('Failed to fetch legal content:', err);
      setError('Failed to load content. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [contentType]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  useEffect(() => {
    navigation.setOptions({
      title: getTitle(),
      headerLeft: () => (
        <TouchableOpacity
          style={styles.backButton}
          onPress={handleBackPress}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Text style={styles.backArrow}>←</Text>
        </TouchableOpacity>
      ),
      ...Platform.select({
        web: {
          headerStyle: { backgroundColor: colors.bg.base },
        },
      }),
    });
  }, [navigation, contentType, styles, colors.bg.base]);

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centeredContainer}>
          <ActivityIndicator size="large" color={semantic.interactive.primary} />
        </View>
      </SafeAreaView>
    );
  }

  if (error) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.centeredContainer}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={fetchContent}>
            <Text style={styles.retryText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator
      >
        {sections.map((section, index) => {
          switch (section.type) {
            case 'heading':
              return (
                <Text key={index} style={styles.heading}>
                  {section.text}
                </Text>
              );
            case 'subheading':
              return (
                <Text key={index} style={styles.subheading}>
                  {section.text}
                </Text>
              );
            case 'paragraph':
              return (
                <Text key={index} style={styles.paragraph}>
                  {section.text}
                </Text>
              );
            default:
              return null;
          }
        })}
        <TouchableOpacity style={styles.browserLink} onPress={handleOpenInBrowser}>
          <Text style={styles.browserLinkText}>Open in Browser</Text>
        </TouchableOpacity>
      </ScrollView>
    </SafeAreaView>
  );
};

const makeStyles = ({ colors, semantic }: ThemeContextValue) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.bg.base,
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 40,
  },
  centeredContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.bg.base,
  },
  heading: {
    fontSize: 22,
    fontWeight: '700',
    color: colors.text.primary,
    marginTop: 24,
    marginBottom: 12,
    fontFamily: Platform.select({ ios: 'Avenir-Heavy', android: 'sans-serif-medium', default: 'System' }),
  },
  subheading: {
    fontSize: 17,
    fontWeight: '600',
    color: colors.text.primary,
    marginTop: 20,
    marginBottom: 8,
    fontFamily: Platform.select({ ios: 'Avenir-Medium', android: 'sans-serif-medium', default: 'System' }),
  },
  paragraph: {
    fontSize: 15,
    lineHeight: 24,
    color: colors.text.secondary,
    marginBottom: 12,
    fontFamily: Platform.select({ ios: 'Avenir-Book', android: 'sans-serif', default: 'System' }),
  },
  browserLink: {
    marginTop: 24,
    alignItems: 'center',
    paddingVertical: 12,
  },
  browserLinkText: {
    fontSize: 14,
    color: semantic.text.link,
    fontWeight: '500',
  },
  errorText: {
    fontSize: 16,
    color: semantic.status.error,
    textAlign: 'center',
    marginBottom: 16,
  },
  retryButton: {
    backgroundColor: semantic.interactive.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 6,
  },
  retryText: {
    // on-fill: interactive.primary is resioBlue in both themes
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '500',
  },
  backButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backArrow: {
    fontSize: 24,
    color: semantic.text.link,
    fontWeight: '400',
  },
});
